import { Plus, X, List } from "@phosphor-icons/react";
import { useDispatch, useSelector } from "react-redux";
import { BUTTON_ROLES, BUTTON_TYPES, EVENT_NAMES, EVENT_PARAMETERS, getPageName } from "src/utils/EventConstants";
import { trackAnalytics } from "src/pages/helpers";
import { setShowSignupToUpgradeModal } from "src/store/slices/onboardingSlice";
import { setOpenLandingPageMenu } from "src/store/slices/HomeSlice";
import { resetThreadData } from "src/store/slices/messageSlice";
import IHLogoLarge from "src/images/logos/inhouse-logo-large.png";

const TryPageTopNav = ({ page }) => {
  const dispatch = useDispatch();
  const openLandingPageMenu = useSelector((state) => state.homeslice.openLandingPageMenu);
  const threadData = useSelector((state) => state.messageSlice.threadData);

  const menuOptions = [
    { name: "Home", link: "https://www.inhouse.ai/" },
    { name: "About", link: "https://www.inhouse.ai/about" },
    { name: "Pricing", link: "https://www.inhouse.ai/pricing" },
  ];
  const queryParams = new URLSearchParams(window.location.search);
  const isLawyerGpt = queryParams.get("gpt_context_id") !== null;

  return (
    <div
      className={`${
        openLandingPageMenu ? "fixed top-0 left-0 w-full h-screen z-[52]" : "relative h-[60px] md:h-[80px]"
      } md:!shadow-none bg-[#FDFBF8] md:h-[80px] flex flex-col md:flex-row md:justify-end md:items-center md:px-[32px] md:py-[16px] transition-all duration-300`}
    >
      {/* Top Bar */}
      <div className="flex items-center h-[60px] px-4 md:px-0 md:h-auto w-full md:w-auto">
        <div className="flex items-center justify-between md:justify-end w-full md:w-auto">
          {/* Left side - icons + logo when menu open (mobile only) */}
          <div className="flex items-center gap-3 flex-1 md:hidden">
            {page === "landing-page" && !openLandingPageMenu && (
              <div
                className="md:hidden flex items-center justify-center w-[40px] h-[40px] rounded-full  hover:bg-[#EBEBEB] cursor-pointer transition-colors"
                onClick={() => dispatch(setOpenLandingPageMenu(true))}
              >
                <List size={22} weight="bold" className="text-[#353535]" />
              </div>
            )}
            {page === "try" && !openLandingPageMenu && (
              <div
                className="bg-[#748A8C14] hover:bg-[#748A8C24] rounded-full border border-[#E5E5E5] flex h-[40px] w-[40px] cursor-pointer md:hidden transition-colors"
                onClick={() => {
                  trackAnalytics(EVENT_NAMES.CLICKED_BUTTON, {
                    [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
                    [EVENT_PARAMETERS.THREAD_ID]: threadData?.id,
                    [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_ICON,
                    [EVENT_PARAMETERS.BUTTON_TEXT]: "New Chat",
                    [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.START_NEW_CHAT,
                  });
                  dispatch(resetThreadData());
                  window.location.href = "/try";
                }}
              >
                <Plus size={20} weight="bold" className="mx-auto my-auto text-[#353535]" />
              </div>
            )}
            {/* Logo on left when menu is open */}
            {openLandingPageMenu && (
              <a href="https://www.inhouse.ai/" target="_blank" rel="noopener">
                <img src={IHLogoLarge} className="w-[90px] h-[38px] object-contain cursor-pointer" alt="Inhouse" />
              </a>
            )}
          </div>

          {/* Center - Logo (mobile only, when menu is closed) */}
          {!openLandingPageMenu && (
            <a data-testid="try-inhouse-logo" href="https://www.inhouse.ai/" target="_blank" rel="noopener" className="flex-shrink-0 md:hidden">
              <img src={IHLogoLarge} className="w-[90px] h-[38px] object-contain cursor-pointer" alt="Inhouse" />
            </a>
          )}

          {/* Right side - auth buttons or close button when menu open */}
          <div className="flex items-center justify-end flex-1 md:flex-initial gap-[8px]">
            {openLandingPageMenu ? (
              <div
                className="md:hidden flex items-center justify-center w-[40px] h-[40px] rounded-full bg-[#F5F5F5] hover:bg-[#EBEBEB] cursor-pointer transition-colors"
                onClick={() => dispatch(setOpenLandingPageMenu(false))}
              >
                <X size={22} weight="bold" className="text-[#353535]" />
              </div>
            ) : (
              <>
                <button
                  data-testid="try-login-btn-topnav"
                  className="hidden md:inline-flex font-['DM_Sans'] font-[500] text-[14px] text-[#0F0F0F] px-4 py-2 rounded-full border border-[#E5E5E5] hover:bg-[#F5F5F5] transition-colors whitespace-nowrap cursor-pointer"
                  onClick={() => {
                    localStorage.setItem("action_type", "signin");
                    dispatch(setShowSignupToUpgradeModal({ show: true, slide: "login" }));
                  }}
                >
                  Log in
                </button>
                <button
                  data-testid="try-signup-btn"
                  className="btn-base btn-secondary text-[14px] px-4 py-2 rounded-full whitespace-nowrap"
                  onClick={() => {
                    localStorage.setItem("action_type", "signup");
                    dispatch(setShowSignupToUpgradeModal({ show: true, slide: "signup" }));
                  }}
                >
                  Sign up
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu Content */}
      {openLandingPageMenu && (
        <div className="flex-1 w-full flex flex-col md:flex-row md:justify-end md:items-center">
          {/* Menu Links */}
          {page === "landing-page" && (
            <div className="flex flex-col gap-2 px-4 py-6 md:hidden border-t border-[#F0F0F0]">
              {menuOptions.map((menu) => (
                <a
                  key={menu.name}
                  href={menu?.link}
                  target="blank"
                  className="py-3 px-4 rounded-lg text-[16px] font-medium font-sans text-[#353535] hover:bg-[#F5F5F5] transition-colors"
                >
                  {menu?.name}
                </a>
              ))}
            </div>
          )}

          {/* Auth Buttons */}
          <div className="flex flex-col gap-3 px-4 py-4 mt-auto md:mt-0 md:flex-row md:gap-2 md:px-0">
            <button
              data-testid="try-login-btn"
              className="secondary-btn w-full md:w-auto py-3 text-[15px] font-medium rounded-full whitespace-nowrap"
              onClick={() => {
                trackAnalytics(EVENT_NAMES.CLICKED_BUTTON, {
                  [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
                  [EVENT_PARAMETERS.THREAD_ID]: threadData?.id,
                  [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_TEXT,
                  [EVENT_PARAMETERS.BUTTON_TEXT]: "Log In",
                  [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.NAVIGATION_NEXT,
                });
                localStorage.setItem("action_type", "signin");
                dispatch(setShowSignupToUpgradeModal({ show: true, slide: "login" }));
              }}
            >
              Log In
            </button>
            <button
              data-testid="try-signup-btn-menu"
              className="btn-base btn-secondary w-full md:w-auto py-3 text-[15px] font-medium rounded-full whitespace-nowrap"
              onClick={() => {
                trackAnalytics(EVENT_NAMES.CLICKED_BUTTON, {
                  [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
                  [EVENT_PARAMETERS.THREAD_ID]: threadData?.id,
                  [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_TEXT,
                  [EVENT_PARAMETERS.BUTTON_TEXT]: "Sign Up",
                  [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.NAVIGATION_NEXT,
                });
                localStorage.setItem("action_type", "signup");
                dispatch(setShowSignupToUpgradeModal({ show: true, slide: "signup" }));
              }}
            >
              Sign Up
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TryPageTopNav;
